import { useState, useCallback, useEffect } from "react";
import { useAxios, APIError } from "../core/_axios";
import { GeneratedPDF } from "../core/_models";
import { adminGetGeneratedPDFs } from "../core/_requests";

const LIMIT = 50; // how many items to request at a time

export function useGeneratedPDFs() {
    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [generatedPDFs, setGeneratedPDFs] = useState<GeneratedPDF[]>([]);
    const [totalGeneratedPDFs, setTotalGeneratedPDFs] = useState<number | null>(null);

    const [offset, setOffset] = useState(0);      // ← replaces currentPage
    const [hasMore, setHasMore] = useState(false);

    /** Core fetcher – always pass the next offset you want to pull. */
    const loadGeneratedPDFs = useCallback(
        async (newOffset: number = 0) => {
            const isLoadingMore = newOffset > 0;
            if (isLoadingMore) {
                setLoadingMore(true);
                setOffset(newOffset);
            } else {
                setLoading(true);
                setGeneratedPDFs([]);
                setOffset(0);
            }
            setLoadingError(null);

            try {
                const {
                    generated_pdfs: fetched,
                    total_generated_pdfs: total,
                } = await adminGetGeneratedPDFs(axios, {
                    limit: LIMIT,
                    offset: newOffset,
                });

                setTotalGeneratedPDFs(total);

                setGeneratedPDFs(prev =>
                    isLoadingMore ? [...prev, ...fetched] : fetched
                );

                // We have more if we *could* fetch another full page
                setHasMore(newOffset + fetched.length < total);
            } catch (err) {
                console.error(err);
                const message =
                    err instanceof APIError
                        ? err.message
                        : "An unknown error has occurred. Please try again.";
                setLoadingError(message);
            } finally {
                isLoadingMore ? setLoadingMore(false) : setLoading(false);
            }
        },
        [axios]
    );

    // initial load
    useEffect(() => {
        loadGeneratedPDFs(0);
    }, [loadGeneratedPDFs]);

    return {
        generatedPDFs,
        totalGeneratedPDFs,
        loading,
        loadingMore,
        loadingError,

        /** Reload everything from scratch */
        reload: () => loadGeneratedPDFs(0),

        /** Pull the next slice */
        loadMore: () => loadGeneratedPDFs(offset + LIMIT),

        hasMore,
        currentOffset: offset,
        limit: LIMIT,
    };
}
